/**
 * Multiple factors can influence the time to download a file. The two most important are download size / upload size, 
 * and your connection bandwidth, colloquially known as network speed. Two other factors are the load of the server you are downloading from, 
 * as well as the speed of your disk drives. 
 * A busy server might not be able to retrieve the files you are requesting quickly enough to fill up your connection,
 *  so even if your bandwidth is 100 mbps the server might only be able to fill 40 mbps of it, resulting in a much slower actual download. 
 * If you have a super speedy connection like 1 gbps, it is possible that your 
 * disk drives may not be speedy enough to catch up in writing the data, again, resulting in slower download than the connection allows.
 */
import eventData from '../../test-data/event-data';
import {DiagnosticDataTransferReq, StressResult, StressSummary } from '../../Types';
import DeviceDiagnosticService from '../../Services/DeviceDiagnosticService';
import {getResource,put,getResourceDownloadProgress} from '../../Services/NetworkService';
import { AppConfigExtended, IADPState} from '../types';
import { environment } from '../util';
export class StressClient {
    private runningTest = false;
    private loopInterrupt = false;
    private mockEventData: any;
    private signedUri : string | undefined = undefined;
    static config: AppConfigExtended;
    private state: IADPState
    constructor(config: AppConfigExtended,appState: IADPState) { 
        StressClient.config = config;
        this.state = appState;
        this.mockEventData = this.getRandomPayload();
      
    }

    public async run(): Promise<StressSummary> {
        const DeviceValidation = this.state?.appState?.configuration?.DeviceValidation
        if (this.runningTest) {
            console.log(`stress-test already running...returning empty results`);
            return {} as StressSummary;
        }
        if (!DeviceValidation?.stressTest.enabled) {
            console.log(`stress-test is not enabled...returning empty results`);
            return {} as StressSummary;
        }
        const totalDownloadImages = StressClient.config.stressTestConfig.files.download.length;

        this.loopInterrupt = false;
        this.runningTest = true;
        const startDate = new Date();
        const details: Array<StressResult> = [];
        let failureCount = 0;
        let successCount = 0;
        let resultId = 0;
        let pingSec = 0;

        console.log("ping health API");
        const pingStart = Date.now();
        try {
            await getResource(`${environment.resolveApiDomain()}/${this.state.appState.configuration?.onlineServices?.cloudServiceHealth}`,15000); 
            const pingEnd = Date.now();
            pingSec = (pingEnd-pingStart)/1000
        }
        catch(e){
           console.log("Ping error",e);
            
        }
        console.log(`stress-test starting with configuration...\n${JSON.stringify(StressClient.config)}`);
        while (this.runningTest) {
            let hasError = false;

            if (this.loopInterrupt || Date.now() - startDate.getTime() > StressClient.config.stressTestConfig.executionTime || successCount >= StressClient.config.stressTestConfig.maxExecutionSets) {
                this.runningTest = false;
                break;
            }

            const downloadFile = StressClient.config.stressTestConfig.files.download[Math.floor(Math.random() * totalDownloadImages)];
            let downloadStart: number;
            let downloadEnd;
            let downloadTimeSec;
            let uploadStart;
            let uploadEnd;
            let uploadTimeSec;
            let response:any;
            let downloadSpeedKbps = 0;
            let uploadSpeedKbps = 0;
            let downloadEtaSec = 0;
            let uploadEtaSec = 0;
            let downloadSize = 0;
            let actualDownloadSize = 0; //total download size within the timeout
            let uploadSize = 0;
            let actualUploadSize = 0; //total upload size within the timeout
            const errors = [];
            const url = downloadFile.url;
            const downloadTimeout = downloadFile.timeoutinMs;
            downloadStart = Date.now();
            downloadSize = downloadFile.fileSizeBits//this.formatBytes(response?.headers['content-length'],3);
            try {
                console.log(`stress-test begin downloading file..${url}`);
                //keep track of all bytes downloaded within allocated time
                const onProgress = (bytes:number)=>{
                    console.log("on download progress", bytes);
                    actualDownloadSize = bytes;
                }
                response = await getResourceDownloadProgress(`${environment.resovleAppDomain()}${url}`, downloadFile.timeoutinMs,onProgress);
                if(response?.status){
                    console.log(`stress-test completed downloading file..${url}`);
                    downloadEnd = Date.now();
                    actualDownloadSize = this.formatBytes(actualDownloadSize);
                    downloadSpeedKbps =this.calculateTransferSpeed(downloadStart, downloadEnd, downloadSize);
                    downloadTimeSec= (downloadEnd - downloadStart)/1000;
                    downloadEtaSec = this.calculateETA(downloadSize, StressClient.config.stressTestConfig.minRequiredDownloadSpeedKbps);
                    if(downloadTimeSec > downloadEtaSec) {
                        hasError = true;
                        errors.push("Download did not complete within configured ETA threshold");
                        failureCount = failureCount + 1;
                    } else {
                        hasError = false;
                        successCount = successCount+1
                    }
                }
               
            } catch(err:any) {
                console.log("on error", err.error, err.progress)
                //don't mark timeout as failure if passess ETA,it is not expected for large files to be download within 15 seconds
                 downloadEnd = Date.now();
                if(err.error.toString()?.includes("timeout")){
                    //add 4% correction to actualDownloadSize
                    const adjustment =(actualDownloadSize*StressClient.config.stressTestConfig.adjustmentCoefficient)/100;
                    console.log("after adjustment",adjustment)
                    actualDownloadSize = err.progress || actualDownloadSize;
                    actualDownloadSize = this.formatBytes(actualDownloadSize+adjustment);
                    downloadSpeedKbps =this.calculateTransferSpeed(downloadStart, downloadEnd, actualDownloadSize);
                    downloadTimeSec= (downloadEnd - downloadStart)/1000;
                    downloadEtaSec = this.calculateETA(actualDownloadSize, StressClient.config.stressTestConfig.minRequiredDownloadSpeedKbps);
                    if(downloadTimeSec > downloadEtaSec) {
                        errors.push("Download did not complete within configured ETA threshold");
                        hasError = true;
                        failureCount = failureCount + 1;
                    } else {
                        hasError = false;
                        successCount = successCount+1
                    }
                }
                else {
                    errors.push(err.error.message);
                    hasError = true;
                    downloadEtaSec = this.calculateETA(downloadSize, StressClient.config.stressTestConfig.minRequiredDownloadSpeedKbps);
                    failureCount = failureCount + 1;
                }
                
            }
            if (this.loopInterrupt || Date.now() - startDate.getTime() > StressClient.config.stressTestConfig.executionTime || successCount >= StressClient.config.stressTestConfig.maxExecutionSets) {
                this.runningTest = false;
                break;
            }
            uploadSize = eventData.size;
            uploadStart =  Date.now();
            try {
                const onUploadProgress = (bytes:number)=>{
                    //console.log("on upload progress", bytes);
                    actualUploadSize =bytes;
                }
                 const result= await this.sendDataTransfer(eventData,this.state,this.signedUri,onUploadProgress);
                this.signedUri = result.signedUri;
                uploadStart = result.uploadStart;
                uploadEnd = result.uploadEnd;
                actualUploadSize = uploadSize;
                uploadSpeedKbps = this.calculateTransferSpeed(uploadStart, uploadEnd, uploadSize);
                uploadEtaSec = this.calculateETA(uploadSize, StressClient.config.stressTestConfig.minRequiredUploadSpeedKbps);
                uploadTimeSec= (uploadEnd - uploadStart)/1000;
                if(uploadTimeSec > uploadEtaSec) {
                    hasError = true;
                    errors.push("Upload did not complete within configured ETA threshold");
                    failureCount = failureCount + 1;
                } else {
                    hasError = false;
                    successCount = successCount+1
                }
            } catch(err:any) {
                    uploadStart = err.uploadStart;
                    uploadEnd = err.uploadEnd;
                    if(err?.err?.toString()?.includes("timeout")){
                        console.log("actualUploadSize before timeout",actualUploadSize);
                      //  const totalUpload = actualUploadSize;
                     //   const adjustment = (totalUpload*config.getStressTestConfig().adjustmentCoefficient)/100+totalUpload;
                        actualUploadSize = this.formatBytes(actualUploadSize);
                        uploadSpeedKbps = this.calculateTransferSpeed(uploadStart, uploadEnd, actualUploadSize);
                        uploadEtaSec = this.calculateETA(actualUploadSize, StressClient.config.stressTestConfig.minRequiredUploadSpeedKbps);
                        uploadTimeSec= (uploadEnd - uploadStart)/1000;
                        if(uploadTimeSec > uploadEtaSec) {
                            hasError = true;
                            errors.push("Upload did not complete within configured ETA threshold");
                            failureCount = failureCount + 1;
                        } else {
                            hasError = false;
                            successCount = successCount+1
                        }
                    }
                    else {
                        errors.push(err.toString());
                        hasError = true;
                        uploadEnd = Date.now();
                        uploadEtaSec = this.calculateETA(uploadSize, StressClient.config.stressTestConfig.minRequiredUploadSpeedKbps);
                        failureCount = failureCount + 1;
                    }    
            }
            details.push({
                resultId,
                url,
                downloadStart,
                downloadEnd,
                downloadTimeSec,
                downloadSpeedKbps,
                downloadEtaSec,
                downloadSize,
                actualDownloadSize,
                uploadSpeedKbps,
                uploadStart,
                uploadEnd,
                uploadTimeSec,
                uploadEtaSec,
                actualUploadSize,
                uploadSize,
                errors,
                hasError,
                downloadTimeout
            })
            const r = Math.floor(Math.random() * StressClient.config.stressTestConfig.sleepIntervalBetweenSets);
            await this.sleep(r);

            resultId = resultId + 1;
        }

        const endDate = new Date();

        const summary:StressSummary = {
            summaryId: Date.now(),
            pingSec: pingSec,
            startDate,
            endDate,
            details,
            failureCount,
            successCount,
            numberOfDownloads: details.length,
            numberOfUploads: details.length,
            avgDownloadSpeed: this.getAvgSpeed(details, "downloadSpeedKbps").toFixed(3),
            minDownloadSpeed: this.getMinSpeed(details, "downloadSpeedKbps").toFixed(3),
            maxDownloadSpeed: this.getMaxSpeed(details, "downloadSpeedKbps").toFixed(3),
            avgUploadSpeed: this.getAvgSpeed(details, "uploadSpeedKbps").toFixed(3),
            minUploadSpeed: this.getMinSpeed(details, "uploadSpeedKbps").toFixed(3),
            maxUploadSpeed: this.getMaxSpeed(details, "uploadSpeedKbps").toFixed(3),
            units: "Kbps"
        }

        console.log(`stressTest summary: ${JSON.stringify(summary)}`);
        return summary;
    }

    public stop(): void {
        this.loopInterrupt = true;
    }


    private formatBytes(bytes: number, decimals = 2) {
        return  Math.round((bytes/1000) * 100) / 100
    }
    private formatBytesBits(bytes: number, decimals = 2) {
        return  Math.round((bytes/1024) * 100) / 100
    }
    private calculateTransferSpeed(startTime: number, endTime: number, fileSizeKb: number): number {
        
        var speedkBps = ((fileSizeKb*8) / ((endTime - startTime)/1000));//convert kilobyte to kilobits
        console.log("Transfer Speed",fileSizeKb, (endTime - startTime)/1000, speedkBps + " kbps\n");
        return speedkBps;
    }

    private calculateETA(fileSizeKb: number, minimumBandwidthKbps: number): number {
        //Download time = File size / Bandwidth
        const etaInSeconds = (fileSizeKb/(minimumBandwidthKbps/8))//convert kilobit to kilobyte
        console.log("ETA",fileSizeKb,minimumBandwidthKbps, etaInSeconds + " seconds\n");
        return etaInSeconds;//add adjustment to compensate for other call delays
    }

    private getAvgSpeed(details: Array<StressResult>, mode: string): number {
        let sum = 0;
        details.forEach( (result: any) => {
            sum = sum + result[mode];
        })

        return sum/details.length;
    }

    private getMaxSpeed(details: Array<StressResult>, mode: string): number {
        const max: any = details.reduce(function(prev: any, current: any) {
            return (prev[mode] > current[mode]) ? prev : current;
        });
        return max[mode];
    }

    private getMinSpeed(details: Array<StressResult>, mode: string): number {
        const min: any = details.reduce(function(prev: any, current: any) {
            return (prev[mode] < current[mode]) ? prev : current;
        });
        return min[mode];
    }

    private async sleep(milliseconds: number): Promise<string> {
        return new Promise(resolve => {
            setTimeout(() => {
                resolve('')
            }, milliseconds);
        })
    };

 

     sendDataTransfer(data: any,state: IADPState,_signedUri:string | undefined,onUploadProgress?:(bytes:number)=>void): Promise<any> {
        return new Promise(async function (resolve, reject) {
            const fileName = "stress-test-data.json";
            const onSignedUrl = (signedUri:string)=>{
                const uploadStart = Date.now(); 
                if(signedUri){
                 console.log(`stress-test uploading data: ${fileName}`);
                 const uploadResponse = put(signedUri,data,15000,onUploadProgress);
                 uploadResponse.then((result:any)=>{
                     console.log(`stress-test upload response: ${JSON.stringify(result)}`);
                     const uploadEnd = Date.now();
                     resolve({uploadStart:uploadStart,uploadEnd:uploadEnd,signedUri:signedUri});
                 })
                 uploadResponse.catch((e: any)=>{
                   console.warn("stress-test file upload failed", e);
                   const uploadEnd = Date.now();
                   reject({uploadStart:uploadStart,uploadEnd:uploadEnd, err:e});
                 })
               
                }
                else {
                 console.log(`stress-test file upload failed`);
                 const uploadEnd = Date.now();
                 reject({uploadStart:uploadStart,uploadEnd:uploadEnd,err:"failed to get sign URL"});
                }
               };
            if(_signedUri){
                console.log(`stress-test starting data transfer with existing URI`);  
                onSignedUrl(_signedUri);
            }
            else {
                console.log(`stress-test starting data transfer,get signed URI`);
            const payload:DiagnosticDataTransferReq = {
                deliveryType: state.sessionState.selectedAssessmentMode,
                dataType:"DIAGNOSTIC",
                dataSubCategory:"stress-test",
                taskType: "UPLOAD",
                taskMessage: "Diagnostic Data Transfer from CB",
                data: {
                  fileName: fileName,
                  loginId: undefined
                },
                clientDetails: {
                  ip: '127.0.0.1',
                  appName: "ng-enaep-chrome-app",
                  deviceDetails: {
                    deviceName:state.deviceState.deviceId as string,
                    deviceType:"NPE"
                  }
                }
              };
              const uploadStart = Date.now();
            try {
                console.log(`stress-test get signed uri: ${JSON.stringify(payload)}`);
                 const signgUri = `${environment.resolveApiDomain()}/${state.appState.configuration?.onlineServices?.dataTransfer?.sign}`
                  DeviceDiagnosticService.transfer.getSignedUrl(signgUri || "",payload, onSignedUrl)
              
            }
            catch(err:any) {
                const uploadEnd = Date.now();
                console.log(`stress-test file upload failed ${err}`);
                reject({uploadStart:uploadStart,uploadEnd:uploadEnd,err:`stress-test file upload failed ${err.toString()}`});
            }
            }
        })
    }

    getRandomPayload(): any {
        if(StressClient.config.stressTestConfig.eventDataType === "static") {
            return eventData;
        }
      /*   var crypto = require("crypto");
        const payload: any = {};
        for (let i = 0; i < StressClient.config.stressTestConfig.uploadPropertyCount; i++ ) {
            payload["property" + i] = crypto.randomBytes(32).toString('hex');
        }
        return payload; */
    }
}
async function RunStressTest(config: AppConfigExtended,appState: IADPState): Promise<any> {
    const stressClient = new StressClient(config,appState);
    const results = await stressClient.run();
   
    return results;
}
export {RunStressTest}
